import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Lightbulb, Zap, ArrowRight, Check, Shield, Award, Eye, Linkedin } from 'lucide-react';
import '../styles/About.css';
import AbhishekPhoto from '../assets/Abhishek_photo.png';

const About = () => {
  const philosophyPoints = [
    {
      title: "Trust over tactics",
      description: "We prioritise long-term credibility and reliable delivery over short-term gains."
    },
    {
      title: "Ethics over shortcuts",
      description: "Our decisions are governed by responsibility, compliance, and integrity"
    },
    {
      title: "Transparency over noise",
      description: "Clear governance, open communication, and shared visibility drive effective execution."
    }
  ];

  const teamQualities = [
    "A preference for structure",
    "A habit of simplifying complexity",
    "A focus on solving high-value problems",
    "Clear and responsible communication",
    "A commitment to timely, dependable delivery"
  ];

  const workProcess = [
    { step: "Listen", description: "We begin with context, constraints, and goals" },
    { step: "Iterate", description: "Fast, meaningful iterations - momentum matters" },
    { step: "Collaborate", description: "Clear decision points and transparent communication" },
    { step: "Deliver", description: "Every engagement ties back to strategic business priorities" }
  ];

  return (
    <div className="about-page">

      {/* HERO */}
      <section className="about-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="about-hero-content"
          >
            <h1 className="about-hero-title">About Kosmoz</h1>
            <p className="about-hero-subtitle">
              Where strategic clarity meets technical depth
            </p>
          </motion.div>
        </div>
      </section>

      {/* LEADERSHIP SECTION */}
      <section className="section leadership-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Our Leader</h2>
          </motion.div>

          <div className="leadership-content">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="leadership-image-wrapper"
            >
              <div className="leadership-image-container">
                <img
                  src={AbhishekPhoto}
                  alt="Abhishek - Founder & CEO"
                  className="leadership-image"
                />
                <div className="leadership-image-glow"></div>
              </div>
              <div className="leadership-name">Abhishek Nishis</div>
              <div className="leadership-role">Founder</div>
              <a
                href="https://www.linkedin.com/in/nishis/"
                target="_blank"
                rel="noopener noreferrer"
                className="leadership-linkedin"
                aria-label="Connect with Abhishek on LinkedIn"
              >
                <Linkedin size={22} />
                <span>Connect on LinkedIn</span>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="leadership-bio"
            >
              <p className="leadership-bio-text">
                Abhishek is a veteran data and analytics leader with over two decades of experience designing and scaling BI, embedded analytics, AI-driven insights, and data engineering platforms. Formerly a global Product & Engineering executive at Jaspersoft, he has spearheaded high-impact initiatives across product, platform, and M&A-driven growth. He has architected and delivered cloud-native analytics and AI solutions across AWS, Azure, and GCP for some of the world's largest manufacturing, pharmaceutical, financial, and retail organizations. Known for blending open-source technologies, enterprise BI, and modern LLM-powered RAG architectures, he specializes in leveraging AI to transform complex data ecosystems into scalable, acquisition-ready, intelligence-driven products.              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STORY SECTION */}
      <section className="section story-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="story-content"
          >
            <h2 className="section-title">Our Story</h2>

            <p className="story-text">
              Kosmoz was created to provide organizations with a consulting partner that
              combines strategic clarity, technical depth, and modern execution capabilities.
            </p>

            <p className="story-text">
              Our work bridges AI, GTM, and product development so that leaders get a
              unified path forward—not siloed solutions.
            </p>

            <p className="story-text highlight">
              We serve teams that value thoughtful work, structured thinking, and steady,
              measurable progress.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY SECTION */}
      <section className="section philosophy-section">
        <div className="container">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Our Philosophy</h2>
            <p className="section-subtitle">
              Our principles guide everything we do
            </p>
          </motion.div>

          <div className="philosophy-grid">
            {philosophyPoints.map((point, index) => {
              const icons = [Shield, Award, Eye];
              const IconComponent = icons[index];

              return (
                <motion.div
                  key={point.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`philosophy-card philosophy-card-${index + 1}`}
                >
                  <div className={`philosophy-icon philosophy-icon-${index + 1}`}>
                    <IconComponent size={30} />
                  </div>
                  <h3 className="philosophy-title">{point.title}</h3>
                  <p className="philosophy-description">{point.description}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="philosophy-footer"
          >
            We aim for clarity, transparency, and long-term value creation in every engagement.
          </motion.p>
        </div>
      </section>

      {/* TEAM SECTION */}
      <section className="section team-section">
        <div className="container">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">The Team</h2>
            <p className="section-subtitle">
              Our team includes strategists, product leaders, designers, technologists,
              data scientists, Architects and content experts.
            </p>
          </motion.div>

          <div className="team-qualities">
            <h3 className="qualities-title">What they share in common:</h3>

            <div className="qualities-grid">
              {teamQualities.map((quality, index) => (
                <motion.div
                  key={quality}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="quality-item"
                >
                  <Check className="quality-icon" />
                  <span>{quality}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="team-footer"
          >
            We bring cross-functional alignment to projects that require both
            strategic thinking and hands-on execution.
          </motion.p>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="hwk-section">
        <div className="hwk-wrap">
          <motion.div
            className="hwk-head"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="hwk-title">How We Work</h2>
            <p className="hwk-subtitle">Listen → understand → solve</p>
          </motion.div>

          <div className="hwk-grid">
            {workProcess.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="hwk-card"
              >
                <div className="hwk-badge" aria-hidden="true">
                  {index + 1}
                </div>

                <div className="hwk-cardBody">
                  <h4 className="hwk-cardTitle">{item.step}</h4>
                  <p className="hwk-cardText">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;
